import { Building2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { StackedCell } from '../../../../components/table/StackedCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { budgetPlansApi } from '../budgetPlansApi';
import type { BudgetPlanDetail } from '../budgetPlanTypes';
import { PpaModal } from '../components/PpaModal';
import { ppasApi } from '../ppasApi';
import {
  type BudgetPlanDepartmentRow,
  type PpaListItem,
} from '../ppaTypes';

/**
 * One department's PPAs under one budget plan.
 *
 * **This is a ROUTE, and that is the whole point of it.** It shipped on 21 Sep 2026 as a
 * drill-down inside the plan's PPA tab — press a roster row, the table is replaced in place,
 * a Back control above it — and was replaced the same day. The second level had no URL, so
 * it could not be linked to a colleague, bookmarked, opened in a second tab, or returned to
 * with the browser's own Back; and the strip that said whose PPAs these were was a
 * breadcrumb hand-rolled out of a button and two lines of text.
 *
 * Navigating into a folder is what the Repository listing already does, and it does it with
 * routes for exactly these reasons. This is the same move, one suite along.
 *
 * **It is neither of the two table shells' own pages, and that is deliberate.**
 * `<IndexPage>` owes the server-side list contract — a paged `ListQuery`, facets, a filter
 * rail — and `ppas` deliberately has none: the list is scoped to a plan, arrives WHOLE, and
 * searches in memory, which is the 201 file's own exemption. So the page hosts a
 * `<DataTablePanel>`, which is the shell for a table inside something else, and the
 * breadcrumb carries the position.
 *
 * **There is no profile behind a roster row**, which is why the header here states the
 * department rather than linking to one: every figure on that row is computed at read time
 * from the enrolment and the PPAs beneath it, so there is no record to open. A view yields,
 * a record does not.
 */
export function BudgetPlanDepartmentPage() {
  const { planId, orgUnitId } = useParams<{ planId: string; orgUnitId: string }>();
  const navigate = useNavigate();

  const [plan, setPlan] = useState<BudgetPlanDetail | null>(null);
  const [department, setDepartment] = useState<BudgetPlanDepartmentRow | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PpaListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore';
    ppas: PpaListItem[];
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const [showDeleted, setShowDeleted] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    The PLAN and the ROSTER, because this page needs both and neither is derivable from the
    other: the plan carries the number, the stage and the goals its PPAs may align to, and
    the roster is the only thing that can name a department which has filed NOTHING. A page
    reading the name off the first PPA would have no heading on the one department a reader
    most often arrives here to start.
  */
  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!planId || !orgUnitId) return;

      setLoading(true);

      Promise.all([budgetPlansApi.get(planId, signal), ppasApi.roster(planId, signal)])
        .then(([nextPlan, roster]) => {
          if (signal?.aborted) return;

          setPlan(nextPlan);
          setDepartment(roster.find((row) => row.orgUnitId === orgUnitId) ?? null);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this department.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [planId, orgUnitId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /*
    Searched, sorted and paged IN MEMORY over the department's own slice. The endpoint is
    scoped to the plan and unpaged, so the filter sees every row there is — the exemption a
    201-file section takes, and what would end it is paging this server-side.
  */
  const fetchPpas = useCallback(
    async (query: DataTableQuery, signal?: AbortSignal) => {
      if (!planId || !orgUnitId) {
        return { items: [], total: 0, page: query.page, pageSize: query.pageSize };
      }

      const all = await ppasApi.forPlan(planId, showDeleted, signal);
      const mine = all.filter((row) => row.orgUnitId === orgUnitId);

      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? mine.filter(
            (row) =>
              row.name.toLowerCase().includes(term) ||
              row.ppaNumber.toLowerCase().includes(term) ||
              (row.goalName ?? '').toLowerCase().includes(term) ||
              (row.expenseClassLabel ?? '').toLowerCase().includes(term),
          )
        : mine;

      const field = query.sort?.replace(/^-/, '') ?? 'ppaNumber';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        const byField =
          field === 'name'
            ? a.name.localeCompare(b.name)
            : field === 'total'
              ? a.total - b.total
              : a.ppaNumber.localeCompare(b.ppaNumber);

        return descending ? -byField : byField;
      });

      const start = (query.page - 1) * query.pageSize;

      return {
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
        page: query.page,
        pageSize: query.pageSize,
      };
    },
    // `reloadKey` is a dependency so a write re-runs the fetch rather than serving the list
    // the reader's own record is missing from.
    [planId, orgUnitId, showDeleted, reloadKey],
  );

  const ppaState = useDataTable<PpaListItem>({ fetchPage: fetchPpas, defaultSort: 'ppaNumber' });

  const reloadPpas = ppaState.reload;

  /** A write moves the department's own totals too, so both are re-read. */
  const refresh = useCallback(() => {
    setReloadKey((current) => current + 1);
    reloadPpas();
    load();
  }, [reloadPpas, load]);

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setActionError(null);

    try {
      for (const ppa of confirming.ppas) {
        if (confirming.kind === 'delete') {
          await ppasApi.deactivate(ppa.id);
        } else {
          await ppasApi.activate(ppa.id);
        }
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setActionError(
        caught instanceof ApiError ? caught : new ApiError('Could not complete that.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  const lockedReason = plan?.plan.isEditable
    ? null
    : plan
      ? `This plan is ${plan.plan.statusLabel.toLowerCase()}, so its PPAs can no longer be changed.`
      : null;

  const columns = useMemo<DataColumn<PpaListItem>[]>(
    () => [
      {
        /*
          THE ANCHOR IS STACKED, which is the reference's own cell: the PPA's name over its
          number and where the goal sits. A `<StackedCell secondary>` is refused inside
          `<IndexTable>` — the dev error is scoped by `IndexTableSurfaceContext`, which this
          panel does not provide — and it is legal here for the reason a preview table and a
          list row may stack: the column has a header, a width, a sort and a settings entry,
          and both views read the same string.
        */
        key: 'name',
        header: 'PPA',
        width: 300,
        sortable: true,
        render: (row) => (
          <StackedCell
            primary={row.name}
            secondary={[row.ppaNumber, row.sectorName, row.subSectorName]
              .filter(Boolean)
              .join(' · ')}
          />
        ),
      },
      {
        /*
          The FUND SOURCE, which is the PPA's expense class (06 Oct 2026). The band below it
          still runs all three classes: only one of a row's three pairs can be non-zero, and
          the footer sums each class down the department.
        */
        key: 'expenseClassLabel',
        header: 'Fund Source',
        width: 170,
        summary: true,
        render: (row) => <TextCell value={row.expenseClassLabel} />,
      },
      /*
        FOUR BANDS, each an ITEMS / AMOUNT pair — the reference's own header, which reads a
        class's count beside its money rather than asking the reader to hold one and scroll
        for the other. The row already carries every figure: the per-class item counts have
        been on `PpaListItem` since it was written.
      */
      {
        key: 'personnelServicesItems',
        header: 'Items',
        group: 'Personnel Services',
        width: 90,
        align: 'right',
        render: (row) => <TextCell value={String(row.personnelServicesItems)} mono numeric />,
        footer: (rows) => rows.reduce((sum, row) => sum + row.personnelServicesItems, 0),
      },
      {
        key: 'personnelServices',
        header: 'Amount',
        group: 'Personnel Services',
        width: 150,
        align: 'right',
        render: (row) => <TextCell value={formatMoney(row.personnelServices)} mono numeric />,
        footer: (rows) => formatMoney(rows.reduce((sum, row) => sum + row.personnelServices, 0)),
      },
      {
        key: 'mooeItems',
        header: 'Items',
        group: 'MOOE',
        width: 90,
        align: 'right',
        render: (row) => <TextCell value={String(row.mooeItems)} mono numeric />,
        footer: (rows) => rows.reduce((sum, row) => sum + row.mooeItems, 0),
      },
      {
        key: 'mooe',
        header: 'Amount',
        group: 'MOOE',
        width: 150,
        align: 'right',
        render: (row) => <TextCell value={formatMoney(row.mooe)} mono numeric />,
        footer: (rows) => formatMoney(rows.reduce((sum, row) => sum + row.mooe, 0)),
      },
      {
        key: 'capitalOutlayItems',
        header: 'Items',
        group: 'Capital Outlay',
        width: 90,
        align: 'right',
        render: (row) => <TextCell value={String(row.capitalOutlayItems)} mono numeric />,
        footer: (rows) => rows.reduce((sum, row) => sum + row.capitalOutlayItems, 0),
      },
      {
        key: 'capitalOutlay',
        header: 'Amount',
        group: 'Capital Outlay',
        width: 150,
        align: 'right',
        render: (row) => <TextCell value={formatMoney(row.capitalOutlay)} mono numeric />,
        footer: (rows) => formatMoney(rows.reduce((sum, row) => sum + row.capitalOutlay, 0)),
      },
      {
        /*
          The reference pairs this with a PPMP count under one `Total Items` band. There is no
          PPMP module in this product, so the band carries the one figure it can answer rather
          than a column of em dashes beside it — see the note under this table.
        */
        key: 'lineItemCount',
        header: 'Items',
        group: 'Total Items',
        width: 90,
        align: 'right',
        render: (row) => <TextCell value={String(row.lineItemCount)} mono numeric />,
        /*
          TOTALLED, which is what the reference draws under this table and what a column of
          money is read down for. It sums the rows ON SCREEN — the page the reader is looking
          at — which is the same contract `<IndexTable>`'s footer has.
        */
        footer: (rows) => rows.reduce((sum, row) => sum + row.lineItemCount, 0),
      },
      {
        key: 'total',
        header: 'Total',
        width: 160,
        align: 'right',
        sortable: true,
        render: (row) => <TextCell value={formatMoney(row.total)} mono numeric />,
        footer: (rows) => formatMoney(rows.reduce((sum, row) => sum + row.total, 0)),
      },
      {
        /*
          THE CEILING BAND — the reference's own, in its own position: after `Total` and before
          the row's trailing chrome.

          It belongs to the (department, FUND SOURCE) — the expense class — not to one PPA, so
          every row under one class reports the same pair. The reference MERGES the two cells down each fund-source
          group with a `rowSpan`; `<DataTable>` cannot row-span, so the pair is drawn on every
          row instead — the same figures, repeated rather than merged, which is the one part of
          this band that is a shape difference rather than a data one.

          NO FOOTER on either. A ceiling is not summable down this table: several rows share
          one fund's figure, so adding the column would multiply each ceiling by how many PPAs
          happen to draw on it.
        */
        key: 'ceilingApproved',
        header: 'Approved',
        group: 'Ceiling',
        width: 150,
        align: 'right',
        render: (row) => (
          /*
            AN EM DASH for unpriced, and a muted `0.00` for a ceiling of zero. The two are
            different facts — nobody has looked, against a reviewer approving nothing — which
            is the distinction the server refuses to blur, so the column must not either.
          */
          <TextCell
            value={row.ceilingApproved === null ? null : formatMoney(row.ceilingApproved)}
            mono
            numeric
          />
        ),
      },
      {
        key: 'ceilingRemaining',
        header: 'Remaining',
        group: 'Ceiling',
        width: 150,
        align: 'right',
        render: (row) => (
          /*
            NEGATIVE where the department has proposed past its ceiling, and nothing clamps it
            — that overrun is exactly what a reviewer opens this table to find.
          */
          <TextCell
            value={row.ceilingRemaining === null ? null : formatMoney(row.ceilingRemaining)}
            mono
            numeric
          />
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: 110,
        /*
          The ROW's own state, unlike the roster one level up: a PPA is a stored record that
          can be deleted, so it gets the flag the shell hoists to the end — and this table
          lists deleted rows, which must never arrive wearing no marker.
        */
        status: true,
        render: (row) => (
          <StatusText tone={row.isActive ? 'active' : 'inactive'}>
            {row.isActive ? 'Active' : 'Deleted'}
          </StatusText>
        ),
      },
    ],
    [],
  );

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Budget Plans', to: '/procurement/investment-planning/budget-plans' },
    {
      label: plan?.plan.planNumber ?? 'Budget plan',
      to: `/procurement/investment-planning/budget-plans/${planId}`,
    },
    department?.orgUnitName ?? 'Department',
  ];

  if (error && !plan) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading && !plan) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full max-w-4xl rounded-panel" />
          <Skeleton className="h-96 w-full rounded-panel" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      {/*
        THE HEADER PROFILE LAYOUT, with no tabs — which is the reference's own shape for this
        page: an identity strip naming the department, and the table under it.

        It was a hand-rolled page: a bare `<PreviewTable>` of four facts slammed across the
        full width with no card and no heading, a loose primary button floating above the
        table, and a panel with no inset at all. Every one of those is something the shell
        already owns, and reaching past it is what made the page look assembled rather than
        designed.

        The four facts are the HEADER's now, which is where record-level facts belong: they
        are true of the whole page rather than of one section of it.
      */}
      <ProfileHeaderShell
        id="ppa-department"
        header={
          <ProfileHeader
            id="ppa-department-header"
            icon={<Building2 className="h-6 w-6" aria-hidden="true" />}
            title={department?.orgUnitName ?? 'Department'}
            meta={[
              'Programs, Projects and Activities',
              plan?.plan.planNumber ?? '',
            ].filter((v) => v.length > 0)}
            secondary={
              department
                ? [
                    department.progressLabel,
                    `${formatMoney(department.total)} proposed`,
                  ]
                : []
            }
          />
        }
        banner={
          actionError ? (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
            >
              {actionError.message}
            </p>
          ) : department === null && !loading ? (
            <p className="text-xs text-slate-500">
              This office is not enrolled as a budget department, so it is not on this
              plan&rsquo;s roster. Whatever it has already filed is still listed below.
            </p>
          ) : undefined
        }
        /*
          The table owns its own toolbar, pager and totals row, so it takes the panel edge to
          edge — a stack of cards would cap it to a measure it does not want.
        */
        panel="bleed"
      >
        {/*
          `fill` distributes what it is GIVEN, so the wrapper offers the height — and the
          `p-4` is the caller's, because a `bleed` panel gives its child no inset.
        */}
        <div className="flex h-full min-w-0 flex-col p-4">
          <DataTablePanel<PpaListItem>
            id="ppa-department-ppas"
            state={ppaState}
            columns={columns}
            rowKey={(row) => row.id}
            searchPlaceholder="Search PPAs"
            fill
            emptyMessage={`${department?.orgUnitName ?? 'This department'} has filed nothing under this plan yet.`}
            emptySearchMessage="No PPA matches that search."
            settingsItems={[
              {
                key: 'show-deleted',
                label: 'Show deleted',
                checked: showDeleted,
                onClick: () => setShowDeleted((current) => !current),
                section: 'Records',
              },
            ]}
            /*
              SELECTABLE with a bulk delete. `selectable={false}` is only for a table nothing
              can ever be done to — a table whose bulk actions are merely unbuilt is a gap to
              fill, which is how three profile panels came to have no checkbox at all while
              every row in them could be deleted one at a time.
            */
            bulkActions={(rows) => [
              {
                key: 'bulk-delete',
                label: `Delete ${rows.length} PPA${rows.length === 1 ? '' : 's'}`,
                permission: 'ppas.delete',
                destructive: true,
                disabledReason:
                  lockedReason ??
                  (rows.every((row) => row.isActive) ? null : 'Some of these are already deleted.'),
                onClick: () => setConfirming({ kind: 'delete', ppas: rows }),
              },
            ]}
            otherInfo={(row) => row}
            /*
              The anchor OPENS THE PPA, which is where its budgeted lines are added and
              corrected. `ppas.read` is the key of what it opens — narrower than the
              `budget-plans.read` that got the reader this far, which is the whole reason
              those two keys are split.
            */
            onRowOpen={(row) => navigate(`/procurement/investment-planning/ppas/${row.id}`)}
            openPermission="ppas.read"
            /*
              THE STANDARD CREATE. The panel draws it — same glyph, same variant, same gate,
              same disabled-reason tooltip as every other Create in the product. All this page
              says is what the button DOES and what it is called: a bare `Create` over a
              department's proposal reads as creating the department.
            */
            onCreate={() => setCreating(true)}
            createLabel="File a PPA"
            createPermission="ppas.create"
            createDisabledReason={lockedReason}
            onEdit={(row) => setEditing(row)}
            editPermission="ppas.update"
            onDelete={(row) => setConfirming({ kind: 'delete', ppas: [row] })}
            deletePermission="ppas.delete"
            deleteDisabledReason={(row) =>
              row.isActive ? lockedReason : 'This PPA is already deleted.'
            }
            rowActions={(row) =>
              row.isActive
                ? []
                : [
                    {
                      key: `restore-${row.id}`,
                      label: 'Restore',
                      icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
                      permission: 'ppas.delete',
                      disabledReason: lockedReason,
                      onClick: () => setConfirming({ kind: 'restore', ppas: [row] }),
                    },
                  ]
            }
          />
        </div>
      </ProfileHeaderShell>

      {plan && (creating || editing) ? (
        <PpaModal
          open
          budgetPlanId={plan.plan.id}
          planNumber={plan.plan.planNumber}
          orgUnitId={editing?.orgUnitId ?? orgUnitId ?? ''}
          orgUnitName={editing?.orgUnitName ?? department?.orgUnitName ?? 'This department'}
          editing={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => setConfirming(null)}
          onConfirm={() => void runConfirmed()}
          title={
            confirming.kind === 'delete'
              ? `Delete ${confirming.ppas.length === 1 ? 'PPA' : `${confirming.ppas.length} PPAs`}`
              : 'Restore PPA'
          }
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`ppa-${confirming.kind}-dialog`}
          body={
            confirming.kind === 'delete' ? (
              <div className="flex flex-col gap-2">
                <p>
                  {/* Names them where a reader can still check, and counts them where they
                      cannot — a list of forty numbers is not a consequence anyone reads. */}
                  {confirming.ppas.length <= 5
                    ? confirming.ppas.map((p) => p.ppaNumber).join(', ')
                    : `${confirming.ppas.length} PPAs`}{' '}
                  {confirming.ppas.length === 1 ? 'is' : 'are'} kept and can be restored. Their
                  budgeted lines are left exactly as they are, so a restore brings back the
                  whole proposal — and a PPA number is never reissued to another.
                </p>
                <p>
                  This department&rsquo;s proposed total drops by{' '}
                  {formatMoney(confirming.ppas.reduce((sum, p) => sum + p.total, 0))}.
                </p>
              </div>
            ) : (
              <p>
                {confirming.ppas[0]?.ppaNumber} returns to this department&rsquo;s proposal and
                counts toward the plan&rsquo;s totals again.
              </p>
            )
          }
        />
      ) : null}
    </div>
  );
}
